// Config-calibration vs run-solve drift evaluation, shared by RunEntry (full
// mismatch details for the asterisk tooltip) and ConfigEntry (session-wide
// count driving the run-vs-session reprocess recommendation).
//
// Tolerance bands come from session.calibration_drift_bands (server
// WARNING_CONSTS + per-session session_health_vars override). Each band is
// [warn, error]; either stage may be null. Solver snapshot lives under
// solve.inputs.* (see SolveInputs in Run.ts).
//
// The same check flags a run whose session gate changed after it was
// processed. The solve records the gate it was cut with (solve.inputs.gate_id
// and gate_hash, a hash of the coordinates) and the session's gate carries the
// hash of its current coordinates (gates.gate_hash, same function server-side).

import { isSyntheticDynamicPressure } from '../../../utils/effectiveCalibration';
import { CalibrationDriftBands } from 'types/Session';

export type DriftSeverity = 'green' | 'yellow' | 'red';

export interface DriftMismatch {
  field: string;
  configValue: any;
  runValue: any;
  severity: DriftSeverity;
  /** One-line explanation shown in place of the calibration/run value pair. */
  detail?: string;
}

export interface DriftStatus {
  worstSeverity: DriftSeverity;
  mismatches: DriftMismatch[];
}

/** The session's current gate, as far as the drift check needs it. */
export interface SessionGateRef {
  gates_id?: string | null;
  gate_hash?: string | null;
}

export const GATE_CHANGED_DETAIL = 'Session gate changed since this run was processed';
export const GATE_MOVED_DETAIL = 'Gate position moved since this run was processed';

// A run processed against the session's database gate records that gate's id
// and coordinate hash. A different id means the session now points at another
// gate; the same id with another hash means the gate itself was moved by more
// than the hash's ~6 m rounding. Either way the run's laps, route and CdA were
// cut with the old gate and the run needs a reprocess.
//
// Skipped when the run carries no gate id (processed from its own gate marks,
// or before the solve recorded gates) and when the stored hash is a number:
// solves written before the hashlib scheme hold Python hash() integers that
// can never equal the current string hash.
export function gateChangeSince(run: any, sessionGate?: SessionGateRef | null): DriftMismatch | null {
  const runGateId = run?.solve?.inputs?.gate_id;
  if (!sessionGate || !runGateId) return null;
  const sessionGateId = sessionGate.gates_id;
  if (sessionGateId && String(runGateId).toLowerCase() !== String(sessionGateId).toLowerCase()) {
    return {
      field: 'Gate',
      configValue: sessionGateId,
      runValue: runGateId,
      severity: 'red',
      detail: GATE_CHANGED_DETAIL,
    };
  }
  const runHash = run?.solve?.inputs?.gate_hash;
  const sessionHash = sessionGate.gate_hash;
  if (typeof runHash !== 'string' || !runHash || !sessionHash) return null;
  if (runHash !== sessionHash) {
    return { field: 'Gate', configValue: sessionHash, runValue: runHash, severity: 'red', detail: GATE_MOVED_DETAIL };
  }
  return null;
}

export function checkValueMatches(
  run: any,
  config: any,
  driftBands?: CalibrationDriftBands,
  sessionGate?: SessionGateRef | null,
): DriftStatus {
  const mismatches: DriftMismatch[] = [];
  let worstSeverity: DriftSeverity = 'green';

  const evalDrift = (
    field: string,
    configValue: number | undefined,
    runValue: number | null | undefined,
    band: [number | null, number | null] | undefined,
  ) => {
    if (configValue === undefined || runValue === undefined || runValue === null || !band) {
      return;
    }
    const [warn, error] = band;
    const diff = Math.abs(configValue - runValue);
    let severity: DriftSeverity = 'green';
    if (error != null && diff >= error) {
      severity = 'red';
    } else if (warn != null && diff >= warn) {
      severity = 'yellow';
    }
    if (severity !== 'green') {
      mismatches.push({ field, configValue, runValue, severity });
      if (severity === 'red' || (worstSeverity === 'green' && severity === 'yellow')) {
        worstSeverity = severity;
      }
    }
  };

  // Synthetic-DP runs have no probe, so the pitot-derived parameters are
  // meaningless there: the solve deliberately uses cp_corr = 1 and the yaw
  // offset has nothing to calibrate against. Comparing either against the
  // config value is not drift. Wheel circumference is probe-independent and
  // is always checked.
  if (!isSyntheticDynamicPressure(run)) {
    evalDrift('CP Correction', config?.cp_corr, run?.solve?.inputs?.cp_corr,
      driftBands?.cp_corr_diff);
    evalDrift('Yaw Offset', config?.yaw_offset, run?.solve?.inputs?.yaw_offset,
      driftBands?.yaw_offset_diff);
  }
  evalDrift('Wheel Circumference', config?.wheel_circumference,
    run?.solve?.inputs?.wheel_circumference, driftBands?.wheel_circumference_m_diff);

  const gateChange = gateChangeSince(run, sessionGate);
  if (gateChange) {
    mismatches.push(gateChange);
    worstSeverity = 'red';
  }

  return { worstSeverity, mismatches };
}
